// Local
import Label from '../../Label';
import { FieldProgressBar } from '../../FieldProgressBar';
import { get, useFormContext, useFormState } from 'react-hook-form';
import { ErrorContent } from '@core-web/typography';
import { Icon } from '@core-web/ui';
import { IconNames } from '@core-web/ui/Icon';
import { IBaseInput } from '../globals/types';
import { useEffect, useState, memo, useId } from 'react';
import * as S from '../globals/styles/Input.styled';
import { useEditablePageContext, usePageState } from '@echotex/business-modules';
import { Operation } from '@echotex/api-merchandising';

const ErrorInputIcon = (): JSX.Element => (
  <Icon
    icon={IconNames.CROSS_20}
    sx={{
      display: 'flex',
      position: 'absolute',
      left: 'calc(100% + 12px)',
      top: '24px',
      transform: 'translateY(-50%)',
    }}
  />
);

const SuccessInputIcon = (): JSX.Element => (
  <Icon
    icon={IconNames.CHECK_18}
    sx={{
      display: 'flex',
      position: 'absolute',
      left: 'calc(100% + 12px)',
      top: '24px',
      transform: 'translateY(-50%)',
    }}
  />
);

export const errorChecker = (errors: any, name: string) => {
  return get(errors, name) ?? null;
};

const BaseInputForMemo = memo(({ input, ...props }: IBaseInput): JSX.Element => {
  const {
    shouldRenderErrorIcon,
    shouldShowSuccessIcon,
    loading,
    disabled,
    required,
    name,
    styles,
    label,
    defaultValue,
    hideErrorMessage,
    error,
    ...restProps
  } = props;

  const { clearErrors } = useFormContext();
  const [showSuccessIcon, setSuccessIcon] = useState(false);

  // page state
  const pageState = usePageState();
  const editablePageState = useEditablePageContext();
  const isEditMode = !!pageState?.selectedItem;
  const reactId = useId();
  const id = `${reactId}-${name}`;
  const InputChildren = input;

  const isDisabled = disabled || loading || false;
  const isRequired = required || false;

  const shouldShowErrorIcon = shouldRenderErrorIcon || false;
  const errorIcon = shouldShowErrorIcon ? <ErrorInputIcon /> : false;
  const successIcon = shouldShowSuccessIcon ? <SuccessInputIcon /> : false;

  const hasError = !!error;

  const [isReadOnlyOnEdit, setReadOnlyOnEdit] = useState(isDisabled);

  const handleFocus = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    clearErrors(name);
    setSuccessIcon(false);
    restProps?.onFocus?.(e);
  };

  const handleBlur = async (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (shouldShowSuccessIcon) setSuccessIcon(!hasError);
    restProps?.onBlur?.(e, restProps?.dependentFields);
  };

  const additionalCallBack = !shouldShowSuccessIcon ? { onBlur: handleBlur } : {};

  useEffect(() => {
    if (editablePageState?.fieldConfigurations?.developmentNameId) {
      const fieldList = editablePageState.fieldConfigurations.fieldNameList;
      const nameSplitted = name.split('.');
      const extractedName = nameSplitted[nameSplitted.length - 1];
      const markedField = fieldList.find((item) => item.nonEditableFieldName === extractedName);
      if (markedField) {
        const shouldDisableOnEdit =
          markedField.operation === Operation.INSERT || (isEditMode && markedField.operation === Operation.UPDATE);
        setReadOnlyOnEdit(shouldDisableOnEdit);
      }
    }
  }, [editablePageState?.fieldConfigurations?.developmentNameId]);

  return (
    <>
      {restProps.type === 'hidden' ? (
        <InputChildren {...restProps} name={name} id={id} defaultValue={defaultValue} />
      ) : (
        <S.StyledFormControl sx={styles?.control}>
          {label && (
            <Label
              label={label}
              disabled={isDisabled || isReadOnlyOnEdit}
              required={isRequired}
              error={!!error}
              htmlFor={id}
            />
          )}

          <S.InputWrapper>
            <InputChildren
              {...restProps}
              name={name}
              styles={styles}
              label={label}
              //className={inputValue ? 'withValue' : ''}
              onFocus={handleFocus}
              error={error}
              disabled={isDisabled || isReadOnlyOnEdit}
              required={isRequired}
              id={id}
              defaultValue={defaultValue}
              {...additionalCallBack}
            />

            {hasError && shouldShowErrorIcon && errorIcon}
            {!hasError && showSuccessIcon && shouldShowSuccessIcon && successIcon}
          </S.InputWrapper>

          {loading && <FieldProgressBar asyncValidating={loading} />}

          {!hideErrorMessage && error && error.message && (
            <S.ErrorWrapper>
              <ErrorContent title={error.message?.toString()} />
            </S.ErrorWrapper>
          )}
        </S.StyledFormControl>
      )}
    </>
  );
});

BaseInputForMemo.displayName = 'BaseInputForMemo';

const BaseInput = (props: IBaseInput): JSX.Element => {
  const { name } = props;
  const { errors } = useFormState({ name, exact: true });
  const errorObj = name && errors && errorChecker(errors, name);
  return <BaseInputForMemo {...props} error={errorObj} />;
};

export default BaseInput;
